import { useState, useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { DASHBOARD_QUERY_KEY } from "./useDashboardData";
import { useAccessibleCircles } from "./useAccessibleCircles";
import { useAuth } from "../context/AuthContext";
import { useLocation } from "react-router-dom";
import { useUsers } from "./useUsers";
import {
  FILL_IN_THE_BLANKS_STEP,
  flowHasPublishConfiguredBeforeReview,
  noticeFlows,
  stepNumberNamed,
} from "../config/noticeFlows";
import { NoticeFlow, NoticeType } from "../types/notice";
import { api } from "../services/api";
import { convertAssetPathToUrl, DEFAULT_NOTICE_BACKGROUND } from "../config/assets";
import {
  dashboardPublicAssetFetchPath,
  isDashboardPublicAssetUrl,
} from "../constants/premadeNoticeTemplates";
import { useSnackbar } from "../context/SnackbarContext";
import { buildNoticePayload, noticeDriveFolderKey } from "../domain/notices/noticePayload";
import type { WizardStepName } from "../domain/notices/wizardSession";
import {
  INTERNAL_TEAM_OPTION_ID,
  noticeAudiencePickPatch,
} from "../domain/notices/noticeAudience";
import {
  uploadFileToPresignedUrl,
  type NoticeUploadProgressState,
  NOTICE_UPLOAD_IN_PROGRESS_MESSAGE,
} from "../utils/presignedUpload";
import { uploadNoticeFiles } from "../services/noticeFileUpload";
import { withAddedAttachments } from "../domain/notices/noticeAttachments";
import { DEFAULT_EVENT_REMINDER_MODES } from "../constants/eventReminderOptions";
import type { EventRecurrenceFrequency } from "../domain/events/eventRecurrence";
import {
  eventDayProblem,
  eventPlaceProblem,
  eventEndProblem,
  eventEndTimeProblem,
  eventTimeProblem,
  eventMessageProblem,
  messageProblem,
  pollChoicesProblem,
  pollQuestionProblem,
  repeatProblem,
  rsvpDeadlineProblem,
  titleProblem,
} from "../domain/notices/noticeRules";
import {
  noticeTypeSupportsScheduledPublish,
  getPublishAtValidationError,
  STAFF_ONLY_SCHEDULE_ERROR,
} from "../utils/noticeScheduling";
import {
  hasPlaceholders,
  placeholdersErrorMessage,
} from "../utils/noticePlaceholders";
import {
  htmlToPlainText,
  noticeBodyVisibleLength,
} from "../utils/noticeBodyHtml";
import { sanitizeNoticeBodyHtml } from "../utils/sanitizeNoticeBodyHtml";
import {
  getFeatureFlagsFromUser,
  isFeatureFlagEnabled,
} from "../utils/featureFlags";
import { audienceRefusalMessage } from "../domain/notices/audienceRefusal";

interface FormData {
  title: string;
  body: string;
  type: string;
  audience: string;
  attachmentUrl: string;
  uploadedFiles?: Array<{
    fileUrl: string; // S3 URL (all files uploaded via presigned URL)
    fileName: string;
    contentType: string;
    size: number;
  }>;
  eventStartDate?: Date | null;
  eventStartTime?: string;
  eventEndDate?: Date | null;
  eventEndTime?: string;
  eventLocation?: string;
  eventBackgroundImage?: string;
  eventFontColor?: string; // Font color for event notices with backgrounds
  eventReminderMode?: string;
  eventReminderModes?: string[];
  targetSubcircle?: string; // New field for targeted notices
  isUrgent?: boolean; // Toggle for making info notices urgent
  pollOptions?: Array<{ optionId: string; label: string; order: number }>;
  pollClosesAt?: string;
  /** claim = each option can only be taken once (disappears after selection) */
  pollMode?: "standard" | "claim";
  rsvpEnabled?: boolean;
  rsvpClosesAt?: string;
  publishMode?: "now" | "schedule";
  publishAt?: string;
  staffOnly?: boolean;
  driveFolderKey?: string;
  eventRecurrenceFrequency?: string;
  eventRecurrenceEndMode?: "count" | "until";
  eventRecurrenceCount?: number;
  eventRecurrenceUntil?: Date | null;
}

interface FormErrors {
  [key: string]: string | undefined;
  title?: string;
  body?: string;
  type?: string;
  audience?: string;
  attachmentUrl?: string;
  eventStartDate?: string;
  eventStartTime?: string;
  eventEndDate?: string;
  eventEndTime?: string;
  eventLocation?: string;
  targetSubcircle?: string; // New field for targeted notices
  pollOptions?: string;
  rsvpClosesAt?: string;
}

const NOTICE_TYPES: readonly NoticeType[] = [
  "info",
  "urgent",
  "event",
  "image",
  "image_notice",
  "custom_event",
  "direct",
  "poll",
];
function isNoticeType(value: any): value is NoticeType {
  return (
    typeof value === "string" && NOTICE_TYPES.includes(value as NoticeType)
  );
}

const getFriendlyPublishErrorMessage = (error: unknown): string => {
  const refusal = audienceRefusalMessage(error);
  if (refusal) return `${refusal} Nothing was posted.`;

  let rawMessage = "Failed to publish notice";

  if (error instanceof Error) {
    rawMessage = error.message;
  } else if ((error as any)?.response?.data?.message) {
    rawMessage = (error as any).response.data.message;
  } else if ((error as any)?.message) {
    rawMessage = (error as any).message;
  }

  const normalizedMessage = rawMessage.toLowerCase();

  if (
    normalizedMessage.includes("failed to fetch") ||
    normalizedMessage.includes("networkerror") ||
    normalizedMessage.includes("load failed") ||
    normalizedMessage.includes("network request failed")
  ) {
    return "Your notice may already be posted. Check View Notices before trying again — sending again can create duplicates.";
  }

  if (
    normalizedMessage.includes("body") &&
    normalizedMessage.includes("null")
  ) {
    return "Your notice is missing some content. Please add a message and try again.";
  }

  if (normalizedMessage.includes("failed to upload")) {
    return "We could not upload your attachment. Please try again.";
  }

  if (
    normalizedMessage.includes("at least 2 event dates") ||
    normalizedMessage.includes("at least two events")
  ) {
    return "This repeat only covers one date. Choose an end date that includes the next time it happens, then publish again. Nothing was posted.";
  }

  if (
    normalizedMessage.includes("timeout") ||
    normalizedMessage.includes("timed out") ||
    normalizedMessage.includes("504") ||
    normalizedMessage.includes("502") ||
    normalizedMessage.includes("gateway")
  ) {
    return "This is taking longer than expected. Check View Notices before trying again — your notice may already be there.";
  }

  return "Your notice could not be confirmed. Check View Notices before trying again — if it's already there, don't send it again.";
};

/** Notes a field's problem from the shared Notice rules (domain/notices/noticeRules), if it has one. */
const setProblem = (errors: FormErrors, field: keyof FormErrors, problem: string | undefined) => {
  if (problem) errors[field] = problem;
};

/**
 * A blank form, used both for the first mount and as the seed for a new
 * handover: a second handover must never inherit an earlier one's answers.
 */
const freshFormData = (type?: NoticeType): FormData => ({
  title: "",
  body: "",
  type: type || "info",
  audience: "",
  attachmentUrl: "",
  uploadedFiles: [],
  eventStartDate: null,
  eventStartTime: "",
  eventEndDate: null,
  eventEndTime: "",
  eventLocation: "",
  eventBackgroundImage: DEFAULT_NOTICE_BACKGROUND,
  eventFontColor: "",
  eventReminderMode: DEFAULT_EVENT_REMINDER_MODES[0],
  eventReminderModes: [...DEFAULT_EVENT_REMINDER_MODES],
  targetSubcircle: "",
  isUrgent: false,
  pollOptions: [
    { optionId: "opt-0", label: "", order: 0 },
    { optionId: "opt-1", label: "", order: 1 },
  ],
  pollClosesAt: "",
  pollMode: "standard",
  rsvpEnabled: false,
  rsvpClosesAt: "",
  publishMode: "now",
  publishAt: "",
  staffOnly: false,
  driveFolderKey: "",
  eventRecurrenceFrequency: "none",
  eventRecurrenceEndMode: "until",
  eventRecurrenceCount: 10,
  eventRecurrenceUntil: null,
});

export const useNoticeForm = (
  noticeTypeFromProps?: NoticeType,
  onSuccess?: () => void,
  onDataRefresh?: () => void,
  initialFormData?: {
    title?: string;
    body?: string;
    eventBackgroundImage?: string;
    eventFontColor?: string;
    eventReminderMode?: string;
    eventReminderModes?: string[];
    audience?: string;
    targetSubcircle?: string;
    /** Internal team only (Events) */
    staffOnly?: boolean;
    isUrgent?: boolean;
    templateId?: string;
    draftId?: string; // ID of the draft to delete when sending
    eventStartDate?: Date | null;
    eventStartTime?: string;
    eventEndDate?: Date | null;
    eventEndTime?: string;
    eventLocation?: string;
    uploadedFiles?: Array<{
      fileUrl: string;
      fileName: string;
      contentType: string;
      size: number;
    }>;
    /** When set, notice is delivered only to this user (direct notice flow). */
    targetUserId?: string;
    rsvpEnabled?: boolean;
    eventRecurrenceFrequency?: EventRecurrenceFrequency;
    eventRecurrenceUntil?: Date | null;
    pollOptions?: Array<{ optionId: string; label: string; order: number }>;
    pollClosesAt?: string;
    pollMode?: "standard" | "claim";
  },
  flowIdFromProps?: string,
  /** Open on Fill in the values before the flow's own steps. */
  startsWithBlanks = false,
  /** Open on this step, found once the flow's step list is known. */
  startAt?: WizardStepName,
) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatusMessage, setUploadStatusMessage] = useState("");
  const [uploadProgress, setUploadProgress] =
    useState<NoticeUploadProgressState | null>(null);
  const uploadAbortRef = useRef<AbortController | null>(null);
  const [formData, setFormData] = useState<FormData>(() =>
    freshFormData(noticeTypeFromProps),
  );
  const formDataRef = useRef(formData);
  // The prefill whose Internal team only has been applied, so a re-run never undoes a later pick
  const staffOnlyAppliedFor = useRef<typeof initialFormData>(undefined);
  // The prefill whose Event settings (reminders, RSVP, repeat) have been applied, so a re-run keeps later changes
  const eventSettingsAppliedFor = useRef<typeof initialFormData>(undefined);
  // What the form was last set up for; a change in any of these is a new handover,
  // not just a re-render, so nothing from before it carries over.
  const lastHandover = useRef<{
    noticeType?: NoticeType;
    flowId?: string;
    initialFormData?: typeof initialFormData;
  } | null>(null);
  formDataRef.current = formData;
  const [errors, setErrors] = useState<FormErrors>({});
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [recipientCount, setRecipientCount] = useState<number>(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- used in handleConfirmSubmit
  const [pendingSubmit, setPendingSubmit] = useState<React.FormEvent | null>(null);
  const [currentFlow, setCurrentFlow] = useState<NoticeFlow>(noticeFlows.info);
  const { token, user } = useAuth();
  const { subcircles: driveCircles } = useAccessibleCircles();
  const { showMessage } = useSnackbar();
  const notifyUploadInProgress = useCallback(() => {
    showMessage({
      message: NOTICE_UPLOAD_IN_PROGRESS_MESSAGE,
      severity: "info",
    });
  }, [showMessage]);
  const blockIfUploading = useCallback((): boolean => {
    if (!isUploading) return false;
    notifyUploadInProgress();
    return true;
  }, [isUploading, notifyUploadInProgress]);
  const { data: users = [] } = useUsers();
  const location = useLocation();
  const queryClient = useQueryClient();
  const getSingleDirectAutoTitle = useCallback(() => {
    const schoolName = user?.schoolName?.trim();
    if (schoolName) {
      return `Direct Message from ${schoolName}`;
    }
    return "Direct Message from Noticeboard";
  }, [user?.schoolName]);

  useEffect(() => {
    // A new handover (a different type/flow, or a new prefill object) starts
    // the form over; an unrelated re-run (navigation, parent re-render) of
    // the same handover keeps whatever's been typed since.
    const isNewHandover =
      lastHandover.current === null ||
      lastHandover.current.noticeType !== noticeTypeFromProps ||
      lastHandover.current.flowId !== flowIdFromProps ||
      lastHandover.current.initialFormData !== initialFormData;
    lastHandover.current = {
      noticeType: noticeTypeFromProps,
      flowId: flowIdFromProps,
      initialFormData,
    };

    let noticeType = noticeTypeFromProps;
    if (!noticeType) {
      const searchParams = new URLSearchParams(location.search);
      const typeParam = searchParams.get("type");
      noticeType = isNoticeType(typeParam) ? typeParam : undefined;
    }
    // Map "event" to "custom_event" for template compatibility
    if (noticeType === "event") {
      noticeType = "custom_event";
    }
    // "direct" is only for launcher-driven targeted sends (user or circle), not ?type= in the URL
    if (
      noticeType === "direct" &&
      !initialFormData?.targetUserId &&
      !initialFormData?.targetSubcircle &&
      initialFormData?.audience !== "circle"
    ) {
      noticeType = undefined;
    }
    const flow =
      flowIdFromProps && noticeFlows[flowIdFromProps]
        ? noticeFlows[flowIdFromProps]
        : noticeType && noticeFlows[noticeType]
          ? noticeFlows[noticeType]
          : noticeFlows.info;
    
    // Safety check: ensure flow and flow.steps exist
    if (!flow || !flow.steps) {
      console.error("Invalid flow configuration for noticeType:", noticeType);
      return;
    }
    
    let filteredSteps = flow.steps;
    const isSingleUserDirect = flow.id === "direct" && !!initialFormData?.targetUserId;
    if (isSingleUserDirect) {
      filteredSteps = filteredSteps.filter((step) => step.component !== "TitleStep");
    }
    if (startsWithBlanks) {
      filteredSteps = [FILL_IN_THE_BLANKS_STEP, ...filteredSteps];
    }
    
    // Create a modified flow with filtered steps
    const modifiedFlow: NoticeFlow = {
      ...flow,
      steps: filteredSteps,
    };
    
    setCurrentFlow(modifiedFlow);
    if (isNewHandover) {
      setCurrentStep(startAt ? stepNumberNamed(filteredSteps, startAt) : 1);
      setErrors({});
    }
    const startsStaffOnly =
      initialFormData?.staffOnly === true &&
      flow.id === "custom_event" &&
      staffOnlyAppliedFor.current !== initialFormData;
    if (startsStaffOnly) staffOnlyAppliedFor.current = initialFormData;
    const startsWithEventSettings = eventSettingsAppliedFor.current !== initialFormData;
    eventSettingsAppliedFor.current = initialFormData;
    setFormData((prev) => {
      // A new handover is seeded from a blank form, not the previous one's
      // answers; an unrelated re-run seeds from the current state as before.
      const seed = isNewHandover ? freshFormData(noticeTypeFromProps) : prev;
      const next = {
        ...seed,
        type: flow.defaultValues.type,
        audience: initialFormData?.audience || flow.defaultValues.audience,
        targetSubcircle: initialFormData?.targetSubcircle || "",
        eventStartDate: initialFormData?.eventStartDate ?? null,
        eventStartTime: initialFormData?.eventStartTime || "",
        eventEndDate: initialFormData?.eventEndDate ?? null,
        eventEndTime: initialFormData?.eventEndTime || "",
        // Most Events happen at the school, so a new one starts there.
        eventLocation:
          initialFormData?.eventLocation ||
          (flow.id === "custom_event" ? user?.schoolName?.trim() || "" : ""),
        eventBackgroundImage:
          initialFormData?.eventBackgroundImage ??
          flow.defaultValues.eventBackgroundImage ??
          seed.eventBackgroundImage,
        eventFontColor: initialFormData?.eventFontColor || "",
        rsvpEnabled: startsWithEventSettings && initialFormData?.rsvpEnabled ? true : seed.rsvpEnabled,
        ...(startsWithEventSettings && initialFormData?.eventRecurrenceFrequency
          ? {
              eventRecurrenceFrequency: initialFormData.eventRecurrenceFrequency,
              eventRecurrenceEndMode: "until" as const,
              eventRecurrenceUntil: initialFormData.eventRecurrenceUntil ?? null,
            }
          : {}),
        eventReminderMode:
          initialFormData?.eventReminderMode ||
          DEFAULT_EVENT_REMINDER_MODES[0],
        eventReminderModes: initialFormData?.eventReminderModes?.length
          ? [...initialFormData.eventReminderModes]
          : initialFormData?.eventReminderMode &&
              initialFormData.eventReminderMode !== "off"
            ? [initialFormData.eventReminderMode]
            : [...DEFAULT_EVENT_REMINDER_MODES],
        title: isSingleUserDirect
          ? initialFormData?.title?.trim() || getSingleDirectAutoTitle()
          : initialFormData?.title || seed.title || "",
        body: initialFormData?.body || seed.body || "",
        isUrgent:
          initialFormData?.isUrgent || noticeType === "urgent" || false,
      };

      // Do not overwrite in-session edits when this effect re-runs (navigation, parent re-render).
      if (seed.eventStartDate != null) next.eventStartDate = seed.eventStartDate;
      if (seed.eventStartTime?.trim()) next.eventStartTime = seed.eventStartTime;
      if (seed.eventEndDate != null) next.eventEndDate = seed.eventEndDate;
      if (seed.eventEndTime?.trim()) next.eventEndTime = seed.eventEndTime;
      if (seed.eventLocation?.trim()) next.eventLocation = seed.eventLocation;
      if (seed.eventBackgroundImage?.trim()) {
        next.eventBackgroundImage = seed.eventBackgroundImage;
      }
      if (seed.eventFontColor?.trim()) next.eventFontColor = seed.eventFontColor;
      const handsOverReminders = startsWithEventSettings && Boolean(initialFormData?.eventReminderModes?.length);
      if (seed.eventReminderModes?.length && !handsOverReminders) {
        next.eventReminderModes = [...seed.eventReminderModes];
        next.eventReminderMode =
          seed.eventReminderMode || seed.eventReminderModes[0];
      } else if (handsOverReminders) {
        next.eventReminderMode = next.eventReminderModes[0];
      } else if (seed.eventReminderMode === "off") {
        next.eventReminderMode = "off";
        next.eventReminderModes = [];
      }
      if (seed.audience?.trim()) next.audience = seed.audience;
      if (seed.targetSubcircle?.trim()) {
        next.targetSubcircle = seed.targetSubcircle;
      }
      if (seed.staffOnly) next.staffOnly = seed.staffOnly;
      // Internal team only, as a prefill or still picked: the same settings as picking it in the wizard
      if (startsStaffOnly || (seed.staffOnly && flow.id === "custom_event")) {
        Object.assign(
          next,
          noticeAudiencePickPatch(INTERNAL_TEAM_OPTION_ID, { includeStaffOnly: true })
        );
      }
      if (seed.title?.trim() && !isSingleUserDirect) next.title = seed.title;
      if (seed.isUrgent) next.isUrgent = seed.isUrgent;
      if (flow.id === "urgent") next.isUrgent = true;

      // Restore draft attachments on first hydrate, but do not overwrite active in-session edits.
      if (seed.uploadedFiles?.length) {
        next.uploadedFiles = seed.uploadedFiles;
      } else if (initialFormData?.uploadedFiles?.length) {
        next.uploadedFiles = initialFormData.uploadedFiles;
      }
      if (seed.attachmentUrl?.trim()) next.attachmentUrl = seed.attachmentUrl;
      if (flow.id === "poll" || flow.id === "claims") {
        next.publishMode = "now";
        next.publishAt = "";
        if (flow.id === "claims") {
          next.pollMode = "claim";
        }
        const seedHasPollLabels = (seed.pollOptions || []).some(
          (o) => (o.label || "").trim().length > 0,
        );
        if (seedHasPollLabels) {
          next.pollOptions = seed.pollOptions;
          if (seed.pollClosesAt?.trim()) {
            next.pollClosesAt = seed.pollClosesAt;
          }
          if (flow.id === "claims" || seed.pollMode === "claim") {
            next.pollMode = "claim";
          }
        } else if (initialFormData?.pollOptions?.length) {
          const restored = initialFormData.pollOptions.map((o, i) => ({
            optionId: o.optionId || `opt-${i}`,
            label: o.label || "",
            order: typeof o.order === "number" ? o.order : i,
          }));
          // Ensure at least two rows for the poll editor UI.
          while (restored.length < 2) {
            restored.push({
              optionId: `opt-${restored.length}`,
              label: "",
              order: restored.length,
            });
          }
          next.pollOptions = restored;
          if (initialFormData.pollClosesAt?.trim()) {
            next.pollClosesAt = initialFormData.pollClosesAt.trim();
          }
          if (flow.id === "claims" || initialFormData.pollMode === "claim") {
            next.pollMode = "claim";
          }
        } else if (flow.id === "claims") {
          next.pollMode = "claim";
        }
      }
      return next;
    });
  }, [location, noticeTypeFromProps, flowIdFromProps, initialFormData, getSingleDirectAutoTitle, startsWithBlanks, startAt, user?.schoolName]);

  const applyFormPatch = useCallback((patch: Record<string, unknown>) => {
    const keys = Object.keys(patch).filter(Boolean);
    if (keys.length === 0) return;

    setFormData((prev) => ({ ...prev, ...patch }));
    setErrors((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const name of keys) {
        if (next[name]) {
          delete next[name];
          changed = true;
        }
        if (
          name === "audience" ||
          name === "targetSubcircle" ||
          name === "staffOnly"
        ) {
          if (next.audience) {
            delete next.audience;
            changed = true;
          }
          if (next.targetSubcircle) {
            delete next.targetSubcircle;
            changed = true;
          }
        }
        if (name === "publishAt" || name === "publishMode") {
          if (next.publishAt) {
            delete next.publishAt;
            changed = true;
          }
        }
        if (name === "eventStartDate" || name === "eventStartTime") {
          if (next.eventStartDate) {
            delete next.eventStartDate;
            changed = true;
          }
          if (next.eventStartTime) {
            delete next.eventStartTime;
            changed = true;
          }
        }
        if (name === "eventLocation" && next.eventLocation) {
          delete next.eventLocation;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, []);

  const handleChange = useCallback(
    (
      e:
        | React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
          >
        | { target: { name: string; value: any } }
    ) => {
      const { name, value } = e.target;
      if (!name) return;
      applyFormPatch({ [name]: value });
    },
    [applyFormPatch],
  );

  useEffect(() => {
    if (
      formData.type !== "poll" ||
      (formData.publishMode !== "schedule" && !formData.publishAt?.trim())
    ) {
      return;
    }
    handleChange({ target: { name: "publishMode", value: "now" } });
    handleChange({ target: { name: "publishAt", value: "" } });
  }, [formData.type, formData.publishMode, formData.publishAt, handleChange]);

  const handleFileUpload = async (files: FileList) => {
    // Check if this is an image notice type
    const isImageNotice =
      currentFlow.id === "image" ||
      currentFlow.id === "image_notice" ||
      currentFlow.steps.some(
        (step) =>
          step.component === "ImageTitleStep" ||
          step.component === "ImageOnlyTitleStep"
      );

    // If files list is empty, clear uploaded files
    if (files.length === 0) {
      setFormData((prev) => ({
        ...prev,
        uploadedFiles: [],
      }));
      setUploadStatusMessage("");
      setUploadProgress(null);
      setIsUploading(false);
      return;
    }

    setIsUploading(true);
    uploadAbortRef.current = new AbortController();

    try {
      const uploadedFiles = await uploadNoticeFiles(Array.from(files), {
        organizationId: user?.schoolId || user?.orgId,
        token: token || undefined,
        isImageNotice,
        existingCount: formData.uploadedFiles?.length || 0,
        signal: uploadAbortRef.current.signal,
        notify: showMessage,
        onStatus: setUploadStatusMessage,
        onProgress: setUploadProgress,
      });
      if (uploadedFiles.length === 0) return;

      // All notices: append up to the limit; UI shows first image large + list below.
      setFormData((prev) => ({
        ...prev,
        uploadedFiles: withAddedAttachments(prev.uploadedFiles || [], uploadedFiles),
      }));
    } catch (error) {
      console.error("Error in handleFileUpload:", error);
    } finally {
      setUploadStatusMessage("");
      setUploadProgress(null);
      setIsUploading(false);
      uploadAbortRef.current = null;
    }
  };

  const cancelUpload = useCallback(() => {
    uploadAbortRef.current?.abort();
  }, []);

  const handleFileRemove = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      uploadedFiles: prev.uploadedFiles?.filter((_, i) => i !== index) || [],
    }));
  };

  /** Add an existing URL as an attachment without re-uploading (e.g. drive/S3 URLs that fail CORS fetch). */
  const handleAddAttachmentFromUrl = (
    url: string,
    fileName?: string,
    contentType?: string
  ) => {
    const name = fileName || url.split("/").pop() || "image.png";
    const type = contentType || "image/png";
    setFormData((prev) => {
      const currentCount = prev.uploadedFiles?.length || 0;
      if (currentCount >= 3) return prev;
      const shouldReplace =
        currentFlow.id === "image_notice" ||
        currentFlow.steps.some(
          (step) => step.component === "ImageOnlyTitleStep"
        );
      const entry = { fileUrl: url, fileName: name, contentType: type, size: 0 };
      return {
        ...prev,
        uploadedFiles: shouldReplace ? [entry] : [...(prev.uploadedFiles || []), entry],
      };
    });
  };

  // Placeholder helpers: utils/noticePlaceholders (supports {% x %} and {x}).

  const getScheduledPublishError = (): string | undefined => {
    if (formData.publishMode !== "schedule") return undefined;
    return getPublishAtValidationError(formData.publishAt, {
      eventStartDate: formData.eventStartDate,
      eventStartTime: formData.eventStartTime,
    });
  };

  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};
    const currentStepConfig = currentFlow.steps[step - 1];

    switch (currentStepConfig?.component) {
      case "TitleStep":
        setProblem(newErrors, "title", titleProblem(formData.title));
        break;
      case "ImageTitleStep":
        setProblem(newErrors, "title", titleProblem(formData.title));
        // Check if attachment is uploaded or currently uploading
        if (
          (!formData.uploadedFiles || formData.uploadedFiles.length === 0) &&
          !formData.attachmentUrl &&
          !isUploading
        ) {
          newErrors.attachmentUrl = "Don't forget to add an attachment!";
        }
        break;
      case "ImageOnlyTitleStep":
        setProblem(newErrors, "title", titleProblem(formData.title));
        if (hasPlaceholders(formData.body || "")) {
          newErrors.body = placeholdersErrorMessage(formData.body || "");
        }
        // Check if image is uploaded or currently uploading
        if (
          (!formData.uploadedFiles || formData.uploadedFiles.length === 0) &&
          !formData.attachmentUrl &&
          !isUploading
        ) {
          newErrors.attachmentUrl = "Don't forget to add an image!";
        }
        break;
      case "ContentStep":
        if (currentFlow.id === "image") {
          if (
            (!formData.uploadedFiles || formData.uploadedFiles.length === 0) &&
            !isUploading
          ) {
            newErrors.attachmentUrl = "Please upload an image";
          }
        } else if (formData.type === "event") {
          setProblem(newErrors, "body", eventMessageProblem(formData.body || ""));
        } else {
          setProblem(newErrors, "body", messageProblem(formData.body || ""));
        }
        break;
      case "EventInfoStep":
        // Validate event fields for any flow that has EventInfoStep
        setProblem(newErrors, "eventStartDate", eventDayProblem(formData.eventStartDate));
        setProblem(newErrors, "eventStartTime", eventTimeProblem(formData.eventStartTime));
        setProblem(newErrors, "eventLocation", eventPlaceProblem(formData.eventLocation));
        setProblem(newErrors, "eventEndDate", eventEndProblem(formData));
        setProblem(newErrors, "eventEndTime", eventEndTimeProblem(formData));
        setProblem(newErrors, "rsvpClosesAt", rsvpDeadlineProblem(formData));
        break;
      case "EventSettingsStep": {
        const hasRecurrence =
          formData.eventRecurrenceFrequency &&
          formData.eventRecurrenceFrequency !== "none";
        setProblem(
          newErrors,
          "eventRecurrenceFrequency",
          repeatProblem(
            formData.eventRecurrenceFrequency,
            formData.eventRecurrenceUntil,
            formData.eventStartDate,
          ),
        );
        if (!hasRecurrence) {
          if (formData.staffOnly === true && formData.publishMode === "schedule") {
            newErrors.publishAt = STAFF_ONLY_SCHEDULE_ERROR;
          } else {
            const publishError = getScheduledPublishError();
            if (publishError) {
              newErrors.publishAt = publishError;
            }
          }
        }
        break;
      }
      case "DetailsStep":
        // No validation needed for DetailsStep
        break;
      case "PollStep": {
        setProblem(newErrors, "title", pollQuestionProblem(formData.title));
        setProblem(
          newErrors,
          "pollOptions",
          pollChoicesProblem((formData.pollOptions || []).map((o) => o.label)),
        );
        break;
      }
      case "EventPublishStep": {
        if (formData.staffOnly === true && formData.publishMode === "schedule") {
          newErrors.publishAt = STAFF_ONLY_SCHEDULE_ERROR;
        } else if (
          formData.eventRecurrenceFrequency &&
          formData.eventRecurrenceFrequency !== "none" &&
          formData.publishMode === "schedule"
        ) {
          newErrors.publishAt =
            "Recurring events cannot be scheduled for later yet — choose Post now";
        } else if (formData.publishMode === "schedule") {
          const publishError = getScheduledPublishError();
          if (publishError) {
            newErrors.publishAt = publishError;
          }
        }
        break;
      }
      case "ReviewStep":
        if (formData.type !== "direct") {
          const isInternalTeamEvent = formData.staffOnly === true;
          if (!isInternalTeamEvent && !formData.audience) {
            newErrors.audience =
              "Please select who should receive this notice";
          }
          if (
            !isInternalTeamEvent &&
            formData.audience === "subcircle" &&
            !formData.targetSubcircle
          ) {
            newErrors.targetSubcircle = "Please select a target subcircle";
          }
        }
        if (!flowHasPublishConfiguredBeforeReview(currentFlow.id)) {
          if (
            noticeTypeSupportsScheduledPublish(formData.type) &&
            formData.publishMode === "schedule" &&
            formData.type !== "direct"
          ) {
            if (formData.staffOnly === true) {
              newErrors.publishAt = STAFF_ONLY_SCHEDULE_ERROR;
            } else {
              const publishError = getScheduledPublishError();
              if (publishError) {
                newErrors.publishAt = publishError;
              }
            }
          }
        }
        break;
      case "SubcircleSelectionStep":
        if (!formData.targetSubcircle) {
          newErrors.targetSubcircle = "Please select a target subcircle";
        }
        break;
      case "CustomizationsStep":
        // No validation needed for CustomizationsStep
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /** The first step that fails its check (its errors left showing), or undefined. */
  const firstIncompleteStep = (): number | undefined => {
    const index = currentFlow.steps.findIndex((_, i) => !validateStep(i + 1));
    return index === -1 ? undefined : index + 1;
  };

  // Actual submission function - called by handleConfirmSubmit after user confirms,
  // or directly for single-recipient direct notices.
  const performSubmit = async (_e: React.FormEvent) => {
    if (isUploading) {
      blockIfUploading();
      return;
    }

    const submitFormData = formDataRef.current;

    setIsSubmitting(true);
    setShowConfirmation(false);
    setShowModal(true);
    setModalMessage(
      submitFormData.publishMode === "schedule" &&
        noticeTypeSupportsScheduledPublish(submitFormData.type)
        ? "Your notice is being scheduled..."
        : "Your notice is being published..."
    );

    try {
      // For image notices, body is optional (image is uploaded via attachments)
      // Send empty string if body is empty or null/undefined for image notices
      // Ensure body is never null or undefined for any notice type
      const bodyValue = submitFormData.body || "";
      const formattingEnabled = isFeatureFlagEnabled(
        getFeatureFlagsFromUser(user),
        "noticeBodyFormatting",
      );
      const preparedBody = formattingEnabled
        ? sanitizeNoticeBodyHtml(bodyValue).trim()
        : htmlToPlainText(bodyValue).trim();
      const body =
        (submitFormData.type === "image" ||
          submitFormData.type === "image_notice") &&
        noticeBodyVisibleLength(preparedBody) === 0
          ? ""
          : preparedBody;

      // Ensure we have uploaded files or attachmentUrl for image notices
      const hasAttachments =
        (submitFormData.uploadedFiles &&
          submitFormData.uploadedFiles.length > 0) ||
        submitFormData.attachmentUrl;

      if (
        (submitFormData.type === "image" ||
          submitFormData.type === "image_notice") &&
        !hasAttachments
      ) {
        setIsSubmitting(false);
        setShowModal(true);
        setIsSuccess(false);
        setModalMessage(
          "Please upload an image or attachment before submitting."
        );
        return;
      }

      if (submitFormData.type === "poll") {
        const labels = (submitFormData.pollOptions || [])
          .map((o) => o.label.trim())
          .filter(Boolean);
        if (labels.length < 2) {
          setIsSubmitting(false);
          setShowModal(true);
          setIsSuccess(false);
          setModalMessage("Add at least 2 poll options before publishing.");
          return;
        }
      }

      if (
        submitFormData.publishMode === "schedule" &&
        noticeTypeSupportsScheduledPublish(submitFormData.type) &&
        submitFormData.type !== "direct"
      ) {
        const publishError = getPublishAtValidationError(
          submitFormData.publishAt,
          {
            eventStartDate: submitFormData.eventStartDate,
            eventStartTime: submitFormData.eventStartTime,
          }
        );
        if (publishError) {
          setIsSubmitting(false);
          setShowModal(true);
          setIsSuccess(false);
          setModalMessage(publishError);
          return;
        }
      }

      // Ensure all required fields are properly formatted and never null/undefined
      const isSingleUserDirect = !!initialFormData?.targetUserId;
      const singleUserDirectTitle = isSingleUserDirect
        ? submitFormData.title?.trim() || getSingleDirectAutoTitle()
        : undefined;
      const organizationId = user?.schoolId || user?.orgId;
      let resolvedUploadedFiles = submitFormData.uploadedFiles;
      if (resolvedUploadedFiles?.length && token && organizationId) {
        resolvedUploadedFiles = await Promise.all(
          resolvedUploadedFiles.map(async (file) => {
            if (!isDashboardPublicAssetUrl(file.fileUrl)) {
              return {
                ...file,
                fileUrl: convertAssetPathToUrl(file.fileUrl) || file.fileUrl,
              };
            }
            const fetchPath = dashboardPublicAssetFetchPath(file.fileUrl);
            const response = await fetch(fetchPath);
            if (!response.ok) {
              throw new Error("Failed to load template image");
            }
            const blob = await response.blob();
            const contentType = file.contentType || blob.type || "image/png";
            const uploadFile = new File(
              [blob],
              file.fileName || "cover.png",
              { type: contentType }
            );
            const { presignedUrl, fileUrl } = await api.notices.getPresignedUrl(
              uploadFile.name,
              contentType,
              organizationId,
              undefined,
              token
            );
            await uploadFileToPresignedUrl({
              presignedUrl,
              file: uploadFile,
              contentType,
            });
            return {
              fileUrl,
              fileName: uploadFile.name,
              contentType,
              size: uploadFile.size,
            };
          })
        );
      }
      const hasFiles = Boolean(resolvedUploadedFiles?.length || submitFormData.attachmentUrl);
      const defaultDriveFolderKey = noticeDriveFolderKey(
        submitFormData.targetSubcircle,
        driveCircles,
        user?.schoolName,
      );
      if (hasFiles && !submitFormData.driveFolderKey?.trim() && !defaultDriveFolderKey) {
        throw new Error("Couldn't find the selected circle's Drive folder. Please choose the circle again.");
      }
      const noticeData = buildNoticePayload(
        { ...submitFormData, body },
        {
          templateId: initialFormData?.templateId,
          targetUserId: initialFormData?.targetUserId,
          titleOverride: singleUserDirectTitle,
          defaultDriveFolderKey,
          uploadedFiles: resolvedUploadedFiles,
        },
      );

      await api.notices.create(noticeData, token || "");

      // If this was a draft, delete it after successfully creating the notice
      if (initialFormData?.draftId && user?.schoolId) {
        try {
          await api.notices.delete(initialFormData.draftId, user.schoolId, token || "");
        } catch (error) {
          console.error("Failed to delete draft after sending:", error);
          // Don't fail the entire operation if draft deletion fails
        }
      }

      setIsSuccess(true);
      setModalMessage(
        formData.publishMode === "schedule" &&
          noticeTypeSupportsScheduledPublish(formData.type)
          ? "Notice scheduled successfully!"
          : "Notice published successfully!"
      );
      setShowModal(true);

      // Invalidate and refetch all notices data
      queryClient.invalidateQueries({ queryKey: ["notices"] });
      queryClient.invalidateQueries({ queryKey: DASHBOARD_QUERY_KEY });

      // Call the refresh callback if provided
      onDataRefresh?.();

      setFormData({
        title: "",
        body: "",
        type: noticeTypeFromProps || "info",
        audience: "",
        attachmentUrl: "",
        uploadedFiles: [],
        eventStartDate: null,
        eventStartTime: "",
        eventEndDate: null,
        eventEndTime: "",
        eventLocation: "",
        eventBackgroundImage: DEFAULT_NOTICE_BACKGROUND,
        eventFontColor: "",
        eventReminderMode: DEFAULT_EVENT_REMINDER_MODES[0],
        eventReminderModes: [...DEFAULT_EVENT_REMINDER_MODES],
        targetSubcircle: "", // Reset targetSubcircle field
        isUrgent: false, // Reset isUrgent field
        pollOptions: [
          { optionId: "opt-0", label: "", order: 0 },
          { optionId: "opt-1", label: "", order: 1 },
        ],
        pollClosesAt: "",
        pollMode: "standard",
        rsvpEnabled: false,
        rsvpClosesAt: "",
        publishMode: "now",
        publishAt: "",
        staffOnly: false,
        driveFolderKey: "",
        eventRecurrenceFrequency: "none",
        eventRecurrenceEndMode: "count",
        eventRecurrenceCount: 10,
        eventRecurrenceUntil: null,
      });
    } catch (error: any) {
      console.error("Submit error:", error);
      setIsSuccess(false);
      setModalMessage(getFriendlyPublishErrorMessage(error));
      setShowModal(true);
      const raw =
        error instanceof Error
          ? error.message
          : String(error?.message || "publish failed");
      const isValidation =
        /title,\s*body|required|invalid poll|missing required|at least 2 event dates|at least two events/i.test(
          raw,
        );
      void api.ops.reportClientEvent(
        {
          kind: isValidation
            ? "publish_validation_error"
            : "publish_client_failure",
          path: "POST /v1/notices",
          message: raw.slice(0, 500),
          noticeType: submitFormData.type,
        },
        token || ""
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (
    e: React.FormEvent,
    skipQualityCheck = false,
    forceSubmit = false
  ) => {
    e.preventDefault();

    // Final check for placeholder variables before submission
    const placeholderErrors: FormErrors = {};
    if (hasPlaceholders(formData.title)) {
      placeholderErrors.title = placeholdersErrorMessage(formData.title);
    }
    if (formData.body) {
      const plainBody = htmlToPlainText(formData.body);
      if (hasPlaceholders(plainBody)) {
        placeholderErrors.body = placeholdersErrorMessage(plainBody);
      }
    }
    
    if (Object.keys(placeholderErrors).length > 0) {
      setErrors(placeholderErrors);
      // Navigate to the first step with an error; the title is on the
      // first step after Fill in the values.
      if (placeholderErrors.title) {
        setCurrentStep(
          currentFlow.steps.findIndex(
            (step) => step.component !== FILL_IN_THE_BLANKS_STEP.component,
          ) + 1,
        );
      } else if (placeholderErrors.body) {
        // Find the content step
        const contentStepIndex = currentFlow.steps.findIndex(
          step => step.component === "ContentStep"
        );
        if (contentStepIndex !== -1) {
          setCurrentStep(contentStepIndex + 1);
        }
      }
      return;
    }

    if ((!forceSubmit && currentStep !== currentFlow.steps.length) || isUploading) {
      return;
    }
    const incompleteStep = firstIncompleteStep();
    if (incompleteStep !== undefined) {
      setCurrentStep(incompleteStep);
      return;
    }

    if (isSubmitting) return;

    if (blockIfUploading()) {
      return;
    }

    // Single-recipient direct notices: publish immediately (no audience confirmation)
    if (initialFormData?.targetUserId) {
      await performSubmit(e);
      return;
    }

    // Calculate recipient count and show confirmation
    const calculateRecipientCount = async () => {
      try {
        let count = 0;
        const isEntireCircle =
          formData.audience === "circle";

        if (isEntireCircle) {
          // Entire circle - use cached users from useUsers (same as User Management)
          count = users?.length ?? 0;
          setRecipientCount(count);
          setPendingSubmit(e);
          setShowConfirmation(true);
          return;
        }

        if (formData.audience === "subcircle" && formData.targetSubcircle && user?.schoolId && token) {
          // Specific subcircle - get users in that subcircle
          const { users: subcircleUsers } = await api.circles.getSubCircleUsers(
            formData.targetSubcircle,
            user.schoolId,
            token
          );
          count = subcircleUsers?.length ?? 0;
        }

        setRecipientCount(count);
        setPendingSubmit(e);
        setShowConfirmation(true);
      } catch (error) {
        console.error("Error calculating recipient count:", error);
        setRecipientCount(users?.length ?? 0);
        setPendingSubmit(e);
        setShowConfirmation(true);
      }
    };

    calculateRecipientCount();
  };

  const handleNext = () => {
    if (blockIfUploading()) {
      return;
    }
    if (validateStep(currentStep)) {
      setErrors({});
      setCurrentStep((prev) => Math.min(prev + 1, currentFlow.steps.length));
    }
  };

  const handlePrevious = () => {
    setErrors({});
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const handleModalClose = () => {
    setShowModal(false);
    if (isSuccess) {
      // Reset to step 1 for next use
      setCurrentStep(1);
      // Call the onSuccess callback to close the modal and stay on current page
      onSuccess?.();
    }
  };

  const handleConfirmSubmit = () => {
    if (blockIfUploading()) {
      return;
    }
    if (pendingSubmit) {
      performSubmit(pendingSubmit);
      setPendingSubmit(null);
    }
    setShowConfirmation(false);
  };

  const handleCancelSubmit = () => {
    setShowConfirmation(false);
    setPendingSubmit(null);
  };

  return {
    currentStep,
    formData,
    errors,
    showModal,
    modalMessage,
    isSuccess,
    isSubmitting,
    isUploading,
    uploadStatusMessage,
    uploadProgress,
    cancelUpload,
    currentFlow,
    handleChange,
    applyFormPatch,
    handleFileUpload,
    handleFileRemove,
    handleAddAttachmentFromUrl,
    handleSubmit,
    handleNext,
    handlePrevious,
    handleModalClose,
    showConfirmation,
    recipientCount,
    handleConfirmSubmit,
    handleCancelSubmit,
  };
};
